/**
 * Dexie-Schema.
 *
 * Zwei Ebenen von Versionierung, die nicht verwechselt werden duerfen:
 *
 * - `version(n).stores(...)` hier migriert die LAUFENDE Datenbank im Browser.
 * - `src/db/migrations/` migriert EXPORTDATEIEN beim Import.
 *
 * Beides wird gebraucht, beides ist anderer Code.
 *
 * Index-Hinweis: `isRecurring` ist ein gespiegelter `0 | 1`-Wert statt eines
 * Booleans, weil IndexedDB Booleans und `null` nicht zuverlaessig indiziert.
 */
import Dexie, { type EntityTable } from 'dexie';

import { clearDanglingAreas, reconcileLinks } from '@/domain/links/hierarchy';
import { schlafInsMorgenjournal } from '@/domain/journal/sleep';
import { bewertungenAuf10, standardKategorien } from '@/domain/journal/ratings';
import { standardFragen } from '@/domain/journal/reflection';
import type { OberflaecheZeile } from '@/domain/oberflaeche/kacheln';
import type { FocusGoal } from '@/domain/planning/focus';
import type { MeldungsZustand } from '@/domain/benachrichtigungen/zustand';
import { wissenUmstellen } from '@/domain/wissen';
import type { SyncFeldStand, SyncKonflikt } from '@/domain/sync/typen';
import type { Liste, ListenEintrag } from '@/domain/listen/typen';
import type { ErnaehrungsEinstellungen, GegessenTag, Getraenk, Lebensmittel, PlanTag, PlanVorlage, Rezept } from '@/domain/ernaehrung/typen';
import type { Tagesvorlage, VorlagenEinstellungen } from '@/domain/tagesvorlagen/typen';
import type { KoerperGewicht, MuskelGefuehl, SportRadEintrag, PlanEinheit, TrainingsEinstellungen, TrainingsPlan, TrainingsProtokoll, Uebung } from '@/domain/training/typen';
import { neueErfassung, syncErfassung } from '@/db/sync/erfassung';
import { aufzeichnung, neueAufzeichnung } from '@/db/papierkorb/aufzeichnung';
import type { PapierkorbDatei, PapierkorbEintrag, PapierkorbZeile } from '@/domain/papierkorb/typen';
import { DATA_TABLE_NAMES, SYNC_TABLE_NAMES } from '@/db/tabellen';
export { DATA_TABLE_NAMES, PAPIERKORB_SYNC_NAMES, SYNC_TABLE_NAMES, type DataTableName, type SyncTableName } from '@/db/tabellen';

import type {
  Account, Area, Attachment, Book, CalendarEvent, Category, Contact, Envelope, Goal, Habit,
  HabitEntry, JournalEntry, LifeWheelSnapshot, MetaRecord, Project, Resource,
  PriorityColor, SavedColor, Subscription, Task, Transaction, WeeklyReview,
  Subject, SchoolSettings, Grade, StudySession, JournalCategory, Thought, MonthlyReview,
  ReflexionsFrageDef, TerminBestaetigung, JournalLayout,
} from '@/domain/types';

export class OlePrivatDB extends Dexie {
  areas!: EntityTable<Area, 'id'>;
  events!: EntityTable<CalendarEvent, 'id'>;
  tasks!: EntityTable<Task, 'id'>;
  projects!: EntityTable<Project, 'id'>;
  goals!: EntityTable<Goal, 'id'>;
  lifeWheel!: EntityTable<LifeWheelSnapshot, 'id'>;
  weeklyReviews!: EntityTable<WeeklyReview, 'id'>;
  habits!: EntityTable<Habit, 'id'>;
  habitEntries!: EntityTable<HabitEntry, 'id'>;
  journal!: EntityTable<JournalEntry, 'id'>;
  accounts!: EntityTable<Account, 'id'>;
  envelopes!: EntityTable<Envelope, 'id'>;
  transactions!: EntityTable<Transaction, 'id'>;
  subscriptions!: EntityTable<Subscription, 'id'>;
  books!: EntityTable<Book, 'id'>;
  resources!: EntityTable<Resource, 'id'>;
  attachments!: EntityTable<Attachment, 'id'>;
  contacts!: EntityTable<Contact, 'id'>;
  categories!: EntityTable<Category, 'id'>;
  savedColors!: EntityTable<SavedColor, 'id'>;
  priorityColors!: EntityTable<PriorityColor, 'id'>;
  subjects!: EntityTable<Subject, 'id'>;
  schoolSettings!: EntityTable<SchoolSettings, 'id'>;
  grades!: EntityTable<Grade, 'id'>;
  studySessions!: EntityTable<StudySession, 'id'>;
  journalCategories!: EntityTable<JournalCategory, 'id'>;
  thoughts!: EntityTable<Thought, 'id'>;
  monthlyReviews!: EntityTable<MonthlyReview, 'id'>;
  focusGoals!: EntityTable<FocusGoal, 'id'>;
  reflexionsFragen!: EntityTable<ReflexionsFrageDef, 'id'>;
  terminBestaetigungen!: EntityTable<TerminBestaetigung, 'id'>;
  listen!: EntityTable<Liste, 'id'>;
  listenEintraege!: EntityTable<ListenEintrag, 'id'>;
  uebungen!: EntityTable<Uebung, 'id'>;
  trainingsplaene!: EntityTable<TrainingsPlan, 'id'>;
  planEinheiten!: EntityTable<PlanEinheit, 'id'>;
  trainingsprotokolle!: EntityTable<TrainingsProtokoll, 'id'>;
  trainingsEinstellungen!: EntityTable<TrainingsEinstellungen, 'id'>;
  koerpergewicht!: EntityTable<KoerperGewicht, 'id'>;
  muskelgefuehl!: EntityTable<MuskelGefuehl, 'id'>;
  sportRad!: EntityTable<SportRadEintrag, 'id'>;
  lebensmittel!: EntityTable<Lebensmittel, 'id'>;
  rezepte!: EntityTable<Rezept, 'id'>;
  speiseplan!: EntityTable<PlanTag, 'id'>;
  planVorlagen!: EntityTable<PlanVorlage, 'id'>;
  gegessen!: EntityTable<GegessenTag, 'id'>;
  getraenke!: EntityTable<Getraenk, 'id'>;
  ernaehrungsEinstellungen!: EntityTable<ErnaehrungsEinstellungen, 'id'>;
  tagesvorlagen!: EntityTable<Tagesvorlage, 'id'>;
  vorlagenEinstellungen!: EntityTable<VorlagenEinstellungen, 'id'>;
  journalLayouts!: EntityTable<JournalLayout, 'id'>; oberflaeche!: EntityTable<OberflaecheZeile, 'id'>; // N-01: Oberflaechen-Einstellungen, synchronisiert
  /** Gelesen/erledigt/verschoben je Meldung (F-30) - Bedienzustand, nicht in der Sicherung. */
  meldungen!: EntityTable<MeldungsZustand, 'id'>;
  /** Feld-Uhren der Synchronisierung (T-04) - nicht in der Sicherung. */
  syncFelder!: EntityTable<SyncFeldStand, 'schluessel'>;
  /** Konflikthinweise der Synchronisierung (T-04) - nicht in der Sicherung. */
  syncKonflikte!: EntityTable<SyncKonflikt, 'id'>;
  /** Zustand der Aenderungserfassung (T-04), je Datenbank. */
  readonly erfassung = neueErfassung();
  /** Papierkorb (F-45): Kopf und Zeilen (synchronisiert), Bytes geloeschter Anhaenge (nur lokal). */
  papierkorb!: EntityTable<PapierkorbEintrag, 'id'>;
  papierkorbZeilen!: EntityTable<PapierkorbZeile, 'id'>;
  papierkorbDateien!: EntityTable<PapierkorbDatei, 'id'>;
  /** Aufzeichnung fuer Papierkorb und Rueckgaengig (F-45), je Datenbank. */
  readonly aufzeichnung = neueAufzeichnung();
  meta!: EntityTable<MetaRecord, 'key'>;

  constructor(name = 'ole-privat') {
    super(name);

    this.version(1).stores({
      areas: 'id, order, archived',
      events: 'id, start, end, areaId, isRecurring, recurringEventId, sourceTaskId, [areaId+start]',
      tasks: 'id, status, areaId, projectId, plannedDate, dueDate, *contexts, [status+areaId], [plannedDate+order]',
      projects: 'id, status, areaId, goalId',
      goals: 'id, horizon, areaId, periodStart',
      lifeWheel: 'id, date',
      weeklyReviews: 'id, isoWeek',
      habits: 'id, areaId, archived, order',
      habitEntries: 'id, habitId, date, [habitId+date]',
      journal: 'id, date, type',
      accounts: 'id',
      envelopes: 'id, areaId',
      transactions: 'id, date, envelopeId, accountId, [date+envelopeId]',
      subscriptions: 'id, nextDue, areaId',
      books: 'id, status, areaId',
      contacts: 'id, lastContact',
      meta: 'key',
    });

    /**
     * Version 2: eigener Index auf `tasks.order`.
     *
     * `orderBy('order')` kann den zusammengesetzten Index `[plannedDate+order]`
     * nicht verwenden - Dexie braucht dafuer einen einzelnen Index. Nur die
     * Indexliste aendert sich, die Zeilen selbst bleiben, deshalb ist kein
     * `upgrade()` noetig.
     *
     * Achtung: Das ist die Version der LAUFENDEN Datenbank. Das Exportformat
     * (`CURRENT_SCHEMA_VERSION` in `domain/types.ts`) ist davon unabhaengig und
     * bleibt unveraendert, weil sich am Aufbau der Daten nichts aendert.
     */
    this.version(2).stores({
      tasks: 'id, status, areaId, projectId, plannedDate, dueDate, order, *contexts, [status+areaId], [plannedDate+order]',
    });

    /**
     * Version 3: `status` und `parentGoalId` auf `goals` indiziert.
     *
     * Die Zieleansicht filtert auf laufende Ziele und haengt Kindziele unter
     * ihr Jahresziel - beides ohne Index ein voller Tabellendurchlauf. Wieder
     * aendert sich nur die Indexliste, die Zeilen bleiben, also kein
     * `upgrade()`.
     *
     * Das EXPORTFORMAT bleibt bei `CURRENT_SCHEMA_VERSION = 1`: `parentGoalId`
     * ist optional, aeltere Sicherungen importieren unveraendert.
     */
    this.version(3).stores({
      goals: 'id, horizon, areaId, periodStart, status, parentGoalId',
    });

    /**
     * Version 4: Ressourcen und Wissen.
     *
     * `resources` haelt Notizen UND Lesezeichen - sie unterscheiden sich nur
     * durch `url`. `*tags` ist ein multiEntry-Index: damit findet eine Abfrage
     * jede Ressource mit einem bestimmten Schlagwort, ohne alle zu laden.
     *
     * `pinned` ist ein `0 | 1`-Wert statt eines Booleans, weil IndexedDB
     * Booleans nicht zuverlaessig indiziert - dieselbe Falle wie bei
     * `events.isRecurring`.
     *
     * `attachments` liegt getrennt: die Blobs sollen nicht bei jeder
     * Listenabfrage mitgeladen werden.
     */
    this.version(4).stores({
      resources: 'id, kind, bucket, areaId, *tags, pinned, updatedAt, [bucket+areaId]',
      attachments: 'id, resourceId',
    });

    /**
     * Version 5: Verknuepfungen (Roadmap F-11).
     *
     * `tasks.goalId` bekommt einen Index - Aufgaben koennen jetzt direkt an
     * einem Ziel haengen. Das `upgrade()` bringt die BESTEHENDEN Daten einmal
     * nach der neuen Regel in Ordnung: Projekte uebernehmen den Bereich ihres
     * Ziels, Aufgaben den ihres Projekts, und Verweise ins Leere verschwinden
     * (die hinterliessen das alte deleteGoal und deleteArea). Geloescht wird
     * kein einziger Eintrag, nur Verweise.
     *
     * Dieselbe Regel laeuft beim Import alter Sicherungen
     * (`db/migrations`, Exportformat 1 -> 2).
     */
    this.version(5).stores({
      tasks: 'id, status, areaId, projectId, goalId, plannedDate, dueDate, order, *contexts, [status+areaId], [plannedDate+order]',
    }).upgrade(async (tx) => {
      const [areas, goals, projects, tasks, resources, books] = await Promise.all([
        tx.table<Area>('areas').toArray(),
        tx.table<Goal>('goals').toArray(),
        tx.table<Project>('projects').toArray(),
        tx.table<Task>('tasks').toArray(),
        tx.table<Resource>('resources').toArray(),
        tx.table<Book>('books').toArray(),
      ]);
      const areaIds = areas.map((a) => a.id);
      const r = reconcileLinks({ areaIds, goals, projects, tasks });
      const res = clearDanglingAreas(resources, areaIds);
      const buch = clearDanglingAreas(books, areaIds);

      await Promise.all([
        tx.table('goals').bulkPut(r.goals.filter((g) => r.changed.goals.has(g.id))),
        tx.table('projects').bulkPut(r.projects.filter((p) => r.changed.projects.has(p.id))),
        tx.table('tasks').bulkPut(r.tasks.filter((t) => r.changed.tasks.has(t.id))),
        tx.table('resources').bulkPut(res.rows.filter((x) => res.changed.has(x.id))),
        tx.table('books').bulkPut(buch.rows.filter((x) => buch.changed.has(x.id))),
      ]);
    });

    /**
     * Version 6: Deadlines (Roadmap F-09).
     *
     * Projekte und Ziele bekommen ein optionales `deadline`-Feld, indiziert
     * fuer den Kalender und die Uebersicht. Aufgaben hatten `dueDate` schon.
     * Nur neue Indizes, keine Umrechnung - deshalb kein `upgrade()` und kein
     * neues Exportformat: die Felder sind optional, alte Sicherungen passen.
     */
    this.version(6).stores({
      projects: 'id, status, areaId, goalId, deadline',
      goals: 'id, horizon, areaId, periodStart, status, parentGoalId, deadline',
    });

    /**
     * Version 7: Kategorien fuer Termine (Roadmap D-03).
     *
     * Neue Tabelle, Termine bekommen optionale Felder (`categoryId`,
     * `priority`, Markierungen). Keine Umrechnung und kein neues
     * Exportformat: eine alte Sicherung hat einfach keine Kategorien.
     */
    this.version(7).stores({
      categories: 'id, order',
      events: 'id, start, end, areaId, isRecurring, recurringEventId, sourceTaskId, [areaId+start], categoryId',
    });

    /**
     * Version 8: gemerkte Lieblingsfarben (Zusatz zu D-02). Eine Farbe steht
     * hoechstens einmal darin (`&color`). Neue Tabelle, keine Umrechnung.
     */
    this.version(8).stores({
      savedColors: 'id, order, &color',
    });

    /**
     * Version 9: selbst gewaehlte Prioritaetsfarben (V-04). Eine Zeile je
     * Stufe, die vom Standard abweicht. Projekte bekommen `priority` ohne
     * Index - sortiert wird im Speicher. Keine Umrechnung.
     */
    this.version(9).stores({
      priorityColors: 'id',
    });

    /**
     * Version 10: Schule (Roadmap F-22). Faecher und die Einstellungen der
     * Schulseite; Aufgaben bekommen einen Index auf `subjectId`, denn
     * Hausaufgaben sind normale Aufgaben mit Fach. Keine Umrechnung.
     */
    this.version(10).stores({
      subjects: 'id, order',
      schoolSettings: 'id',
      tasks: 'id, status, areaId, projectId, goalId, subjectId, plannedDate, dueDate, order, *contexts, [status+areaId], [plannedDate+order]',
    });

    /** Version 11: Noten (F-22 Stufe 4). Neue Tabelle, keine Umrechnung. */
    this.version(11).stores({
      grades: 'id, subjectId, date',
    });

    /** Version 12: Lernzeiten (F-22 Stufe 7). Neue Tabelle, keine Umrechnung. */
    this.version(12).stores({
      studySessions: 'id, subjectId, start',
    });

    /**
     * Version 13: Schlaf ins Morgenjournal (V-08). Keine neuen Indizes, nur
     * Umzug der Altdaten - dieselbe reine Funktion wie beim Einspielen einer
     * alten Sicherung (db/migrations, Format 2 -> 3).
     */
    this.version(13).stores({}).upgrade(async (tx) => {
      const tabelle = tx.table('journal');
      const neu = schlafInsMorgenjournal(await tabelle.toArray());
      await tabelle.clear();
      await tabelle.bulkPut(neu);
    });

    /**
     * Version 14: Journal-Bewertungen 1-10 mit Kategorien (V-09). Neue
     * Tabelle mit den Standardkategorien; alte 1-5-Werte werden verdoppelt,
     * die Originale bleiben im Eintrag (`legacy5`). Dieselbe reine Funktion
     * wie beim Einspielen alter Sicherungen (Format 3 -> 4).
     */
    this.version(14).stores({
      journalCategories: 'id, journal, order',
    }).upgrade(async (tx) => {
      const journal = tx.table('journal');
      await journal.bulkPut(bewertungenAuf10(await journal.toArray()));
      const kategorien = tx.table('journalCategories');
      if ((await kategorien.count()) === 0) await kategorien.bulkAdd(standardKategorien(new Date().toISOString()));
    });

    /** Version 15: Braindump-Gedanken (F-13). Neue Tabelle, keine Umrechnung. */
    this.version(15).stores({
      thoughts: 'id, date, gemerkt, createdAt',
    });

    /** Version 16: Monatsreflexion (F-14). Neue Tabelle, keine Umrechnung. */
    this.version(16).stores({
      monthlyReviews: 'id',
    });

    /**
     * Version 17: Termine mit Projekten und Aufgaben verknuepfen (F-04 d).
     * Nur neue Indizes auf optionalen Feldern, keine Umrechnung.
     */
    this.version(17).stores({
      events: 'id, start, end, areaId, isRecurring, recurringEventId, sourceTaskId, [areaId+start], categoryId, projectId, *taskIds',
    });

    /**
     * Version 18: Wochen- und Monatsziele (F-16). Neue Tabelle, keine
     * Umrechnung. `[art+key]` findet die Ziele eines Zeitraums.
     */
    this.version(18).stores({
      focusGoals: 'id, [art+key], goalId',
    });

    /**
     * Version 19: Buecher werden Wissensquellen (V-13). Typ "Buch", Bereiche
     * in `areaIds` (mehrere moeglich), Bewertung 1-5 -> 1-10. Dieselbe reine
     * Funktion wie beim Einspielen alter Sicherungen (Format 4 -> 5).
     */
    this.version(19).stores({
      books: 'id, status, typ, *areaIds',
    }).upgrade(async (tx) => {
      const tabelle = tx.table<Book>('books');
      await tabelle.bulkPut(wissenUmstellen(await tabelle.toArray()));
    });

    /**
     * Version 20: Wissen mit Projekten und Zielen verknuepfen (F-19). Nur
     * neue Indizes auf optionalen Feldern, keine Umrechnung.
     */
    this.version(20).stores({
      books: 'id, status, typ, *areaIds, *projectIds, *goalIds',
    });

    /**
     * Version 21: Notizen und Gedanken Bereichen, Zielen und Projekten
     * zuordnen (F-17). Nur neue Indizes auf optionalen Feldern.
     */
    this.version(21).stores({
      resources: 'id, kind, bucket, areaId, *tags, pinned, updatedAt, [bucket+areaId], projectId, goalId',
      thoughts: 'id, date, gemerkt, createdAt, areaId, projectId, goalId',
    });

    /**
     * Version 22: Eigene Reflexionsfragen (F-27). Neue Tabelle mit den fuenf
     * Standardfragen; die Antworten bleiben, wo sie sind.
     */
    this.version(22).stores({
      reflexionsFragen: 'id, order',
    }).upgrade(async (tx) => {
      const fragen = tx.table('reflexionsFragen');
      if ((await fragen.count()) === 0) await fragen.bulkAdd(standardFragen(new Date().toISOString()));
    });

    /** Version 23: Termine bestaetigen (F-29). Neue Tabelle, keine Umrechnung. */
    this.version(23).stores({
      terminBestaetigungen: 'id, eventId, datum',
    });

    /**
     * Version 24: Zustand der Benachrichtigungen (F-30). Neue Tabelle; sie
     * gehoert bewusst nicht zur Sicherung - es ist reiner Bedienzustand.
     */
    this.version(24).stores({
      meldungen: 'id, datum',
    });

    /**
     * Version 25: Eigene Fragen auch im Morgenjournal und in den Reflexionen
     * (F-27). Bisherige Fragen gehoeren zum Abendjournal; die
     * Standardfragen von Woche und Monat legt `ensureReflexionsFragen` an.
     */
    this.version(25).stores({}).upgrade(async (tx) => {
      await tx.table<ReflexionsFrageDef>('reflexionsFragen').toCollection().modify((f) => { f.ort ??= 'abend'; });
    });

    /**
     * Version 26: Synchronisierung (T-04). Feld-Uhren und Konflikthinweise,
     * beide lokal und nicht in der Sicherung. Bestandsdaten bekommen ihre
     * Uhren erst beim Einrichten (`alleMarkieren`).
     */
    this.version(26).stores({
      syncFelder: 'schluessel, offen, tabelle',
      syncKonflikte: 'id, zeit',
    });

    /** Version 27: Eigene Listen (F-33). Neue Tabellen, keine Umrechnung. */
    this.version(27).stores({
      listen: 'id, order, areaId',
      listenEintraege: 'id, listeId, [listeId+order]',
    });

    /** Version 28: Trainingsplaene und Uebungsbibliothek (F-34 A). Neue Tabellen. */
    this.version(28).stores({
      uebungen: 'id, name, typ',
      trainingsplaene: 'id, familie',
      planEinheiten: 'id, planId',
    });

    /** Version 29: Protokoll der durchgefuehrten Einheiten (F-34 B). */
    this.version(29).stores({
      trainingsprotokolle: 'id, datum, planEinheitId',
    });

    /** Version 30: Einstellungen der Trainingsauswertung (F-34 C). */
    this.version(30).stores({
      trainingsEinstellungen: 'id',
    });

    /** Version 31: Koerpergewicht und Muskelgefuehl (F-34 C3). */
    this.version(31).stores({
      koerpergewicht: 'id, datum',
      muskelgefuehl: 'id, datum',
    });

    /** Version 32: Sport-Rad (F-34 D). */
    this.version(32).stores({
      sportRad: 'id, datum',
    });

    /** Version 33: Ernaehrung (F-35). */
    this.version(33).stores({
      lebensmittel: 'id, name, barcode, gruppe, zuletzt',
      rezepte: 'id, name',
      speiseplan: 'id, datum',
      planVorlagen: 'id, name',
      gegessen: 'id, datum',
      getraenke: 'id, datum',
      ernaehrungsEinstellungen: 'id',
    });

    // F-36: Tagesvorlagen und Standardwoche.
    this.version(34).stores({
      tagesvorlagen: 'id, order',
      vorlagenEinstellungen: 'id',
    });

    // F-32: Aufbau von Journal und Reflexion (Bloecke, Abschnitte). Felder
    // bleiben in `reflexionsFragen` - nur neue optionale Spalten, keine Migration.
    this.version(35).stores({
      journalLayouts: 'id',
    });

    // F-45: Papierkorb - neue Tabellen, keine Umrechnung, nicht in der Sicherung (Tabellennamen: db/tabellen.ts).
    this.version(36).stores({ papierkorb: 'id, geloeschtAm', papierkorbZeilen: 'id, eintragId, geloeschtAm, *schluessel', papierkorbDateien: 'id, eintragId' });
    this.version(37).stores({ oberflaeche: 'id' }); // N-01 Teil 2: Seitenleiste (Standardwerte, keine Umrechnung)
    this.use(syncErfassung(new Set<string>(SYNC_TABLE_NAMES), this.erfassung));
    this.use(aufzeichnung(new Set<string>(DATA_TABLE_NAMES)));
  }

  /** Alle Nutzdaten-Tabellen in fester Reihenfolge - Basis fuer Export/Import. */
  get dataTables() {
    return [
      this.areas, this.events, this.tasks, this.projects, this.goals,
      this.lifeWheel, this.weeklyReviews, this.habits, this.habitEntries,
      this.journal, this.accounts, this.envelopes, this.transactions,
      this.subscriptions, this.books, this.contacts, this.resources,
      this.attachments, this.categories, this.savedColors, this.priorityColors,
      this.subjects, this.schoolSettings, this.grades, this.studySessions,
      this.journalCategories, this.thoughts, this.monthlyReviews, this.focusGoals,
      this.reflexionsFragen, this.terminBestaetigungen, this.listen, this.listenEintraege,
      this.uebungen, this.trainingsplaene, this.planEinheiten, this.trainingsprotokolle,
      this.trainingsEinstellungen, this.koerpergewicht, this.muskelgefuehl, this.sportRad,
      this.lebensmittel, this.rezepte, this.speiseplan, this.planVorlagen, this.gegessen, this.getraenke, this.ernaehrungsEinstellungen,
      this.tagesvorlagen, this.vorlagenEinstellungen, this.journalLayouts, this.oberflaeche,
    ];
  }

  /** Was synchronisiert wird: Nutzdaten plus Kopf und Zeilen des Papierkorbs (F-45). */
  get syncTables() { return [...this.dataTables, this.papierkorb, this.papierkorbZeilen]; }
  /** Die drei Papierkorb-Tabellen (F-45) - nie in der Sicherung. */
  get papierkorbTabellen() { return [this.papierkorb, this.papierkorbZeilen, this.papierkorbDateien]; }
}

export const db = new OlePrivatDB();

